import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { api, configurarToken, configurarRenovacion } from '@/shared/api/cliente';
import type { Perfil, Sesion } from '@/shared/api/tipos';
import { notificar } from '@/shared/ui/avisos';
import * as almacen from './almacen';

interface Contexto {
  perfil: Perfil | null;
  cargando: boolean;
  acceder: (correo: string, clave: string) => Promise<void>;
  salir: () => Promise<void>;

  puede: (permiso: string) => boolean;
}

const SesionContexto = createContext<Contexto | null>(null);

export function SesionProvider({ children }: { children: ReactNode }) {
  const [perfil, setPerfil] = useState<Perfil | null>(null);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    configurarToken(almacen.tokenDeAcceso);
    configurarRenovacion(async () => {
      const refresco = almacen.tokenDeRefresco();
      if (!refresco) {
        return null;
      }
      try {
        const sesion = await api.post<Sesion>('/identidad/renovar', { tokenDeRefresco: refresco });
        almacen.guardar(sesion);
        return sesion.tokenDeAcceso;
      } catch {
        almacen.borrar();
        setPerfil(null);
        return null;
      }
    });
  }, []);

  useEffect(() => {
    let vigente = true;

    async function recuperar() {
      if (!almacen.tokenDeAcceso()) {
        setCargando(false);
        return;
      }
      try {
        const suyo = await api.get<Perfil>('/identidad/yo');
        if (vigente) {
          setPerfil(suyo);
        }
      } catch {
        almacen.borrar();
      } finally {
        if (vigente) {
          setCargando(false);
        }
      }
    }

    void recuperar();
    return () => {
      vigente = false;
    };
  }, []);

  const acceder = useCallback(async (correo: string, clave: string) => {
    const sesion = await api.post<Sesion>('/identidad/acceso', { correo, clave });
    almacen.guardar(sesion);

    const suyo = await api.get<Perfil>('/identidad/yo');
    setPerfil(suyo);

    notificar.exito(`BIENVENIDO(A), ${suyo.nombre.toUpperCase()}`);
  }, []);

  const salir = useCallback(async () => {
    try {
      await api.post<void>('/identidad/salida');
    } catch {
    }
    almacen.borrar();
    setPerfil(null);

    notificar.exito('SESIÓN CERRADA CORRECTAMENTE.');
  }, []);

  const puede = useCallback(
    (permiso: string) => {
      if (!perfil) {
        return false;
      }
      if (perfil.administrador) {
        return true;
      }

      if (permiso === 'admin') {
        return false;
      }
      return perfil.permisos.includes(permiso);
    },
    [perfil],
  );

  const valor = useMemo<Contexto>(
    () => ({ perfil, cargando, acceder, salir, puede }),
    [perfil, cargando, acceder, salir, puede],
  );

  return <SesionContexto.Provider value={valor}>{children}</SesionContexto.Provider>;
}

export function useSesion(): Contexto {
  const contexto = useContext(SesionContexto);
  if (!contexto) {
    throw new Error('useSesion debe usarse dentro de <SesionProvider>');
  }
  return contexto;
}
